import { ArrowUpIcon, ArrowDownIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type { PaymentTermsSource } from '@erp/shared-types';
import { Button } from '@/components/ui/button';

interface PaymentTermsPriorityListProps {
  priority: readonly PaymentTermsSource[];
  /** Omit to render the order read-only. */
  onChange?: (priority: PaymentTermsSource[]) => void;
  disabled?: boolean;
}

/** Reorderable list of payment-terms sources; the first source with valid terms wins. */
export function PaymentTermsPriorityList({
  priority,
  onChange,
  disabled = false,
}: PaymentTermsPriorityListProps) {
  const gt = useGT();

  const labels: Record<PaymentTermsSource, string> = {
    invoice: gt('Invoice Pay Terms', { context: 'payment terms source label' }),
    supplier: gt('Supplier Pay Terms', {
      context: 'payment terms source label',
    }),
    company: gt('Company Default', { context: 'payment terms source label' }),
  };

  const descriptions: Record<PaymentTermsSource, string> = {
    invoice: gt('Terms extracted from the invoice document', {
      context: 'payment terms source description',
    }),
    supplier: gt('Terms configured on the supplier record', {
      context: 'payment terms source description',
    }),
    company: gt('Organization-level fallback (default net days)', {
      context: 'payment terms source description',
    }),
  };

  const move = (index: number, direction: 'up' | 'down') => {
    const next = [...priority];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    onChange?.(next);
  };

  return (
    <div className="space-y-1">
      {priority.map((source, index) => (
        <div
          key={source}
          className="flex items-center gap-2 rounded-md border p-2"
        >
          <span className="text-xs font-medium text-muted-foreground w-4">
            {index + 1}.
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium">{labels[source]}</p>
            <p className="text-xs text-muted-foreground">
              {descriptions[source]}
            </p>
          </div>
          {onChange && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={disabled || index === 0}
                aria-label={gt('Move {label} up', {
                  context: 'move priority item up button',
                  label: labels[source],
                })}
                onClick={() => move(index, 'up')}
              >
                <ArrowUpIcon className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={disabled || index === priority.length - 1}
                aria-label={gt('Move {label} down', {
                  context: 'move priority item down button',
                  label: labels[source],
                })}
                onClick={() => move(index, 'down')}
              >
                <ArrowDownIcon className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
